import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { CheckboxChangeEvent } from 'primeng/checkbox';

import { PanelMenuService } from './panel-menu.service';

@Component({
  selector: 'app-panel-menu',
  templateUrl: './panel-menu.component.html',
  styleUrl: './panel-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PanelMenuComponent {
  @Input() set items(menuitems: MenuItem[]) {
    this.menuItems = menuitems;
    this._checkedAllCache = null;
  }
  @Output() itemsChange: EventEmitter<MenuItem[]> = new EventEmitter<MenuItem[]>();
  @Output() toggleItemSummary: EventEmitter<MenuItem> = new EventEmitter<MenuItem>();

  @ContentChild('item') itemTpl: TemplateRef<unknown>;

  menuItems: MenuItem[];
  private _checkedAllCache: boolean | null = null;

  get checkedAll(): boolean {
    if (this._checkedAllCache === null && this.menuItems) {
      this._checkedAllCache = this.panelMenuService.areAllItemsChecked(this.menuItems);
    }
    return this._checkedAllCache || false;
  }

  constructor(
    private panelMenuService: PanelMenuService,
    private cdr: ChangeDetectorRef
  ) {}

  toggleItem(event: Event, item: MenuItem) {
    event.preventDefault();
    event.stopPropagation();

    if (item.items?.length) {
      this.panelMenuService.toggleItem(item, !item.expanded);
    }

    this.itemsChange.emit(this.menuItems);
  }

  toggleItemAndChildren(event: Event, item: MenuItem) {
    event.preventDefault();
    event.stopPropagation();

    const { expanded } = item;

    if (item.items?.length) {
      this.panelMenuService.toggleAll(item.items, !expanded);
      this.panelMenuService.toggleItem(item, !expanded);
    }

    this.menuItems = structuredClone(this.menuItems);
    this.itemsChange.emit(this.menuItems);
  }

  collapseAll = () => {
    this.panelMenuService.toggleAll(this.menuItems, false);
    this.itemsChange.emit(this.menuItems);
  };
  expandAll = () => {
    this.panelMenuService.toggleAll(this.menuItems, true);
    this.itemsChange.emit(this.menuItems);
  };

  checkItem(event: CheckboxChangeEvent | Event, item: MenuItem) {
    if (event instanceof Event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const checked = 'checked' in event ? event.checked : !item['checked'];
    this.panelMenuService.selectItem(item, this.menuItems, checked);
    this._checkedAllCache = null;
    this.cdr.markForCheck();
    this.itemsChange.emit(this.menuItems);
  }

  changeSelectAll({ checked }: CheckboxChangeEvent): void {
    this.menuItems = this.panelMenuService.selectAllItems(this.menuItems, checked);
    this._checkedAllCache = checked;
    this.itemsChange.emit(this.menuItems);
  }
}
